'use client';

import type { PusherIntegrationConfigType } from '@unifyapps/defs/types/interface';
import { type RefObject, useCallback, useRef } from 'react';
import type { PusherCallback } from '../../consts/pusher/callbacks';
import { PUSHER_CHANNEL_PREFIX } from '../../consts/pusher/channelType';
import type { PusherChannel, PusherClient } from '../../consts/pusher/sdk';
import { usePusherChannelEvents } from './useChannelEvents';

interface UsePusherChannelActionsParams {
  clientRef: RefObject<PusherClient | undefined>;
  pusherIntegrationConfig?: PusherIntegrationConfigType;
  publishPusherCallback: (eventName: 'onPusherCallback', event: PusherCallback) => void;
}

export function usePusherChannelActions({
  clientRef,
  pusherIntegrationConfig,
  publishPusherCallback,
}: UsePusherChannelActionsParams) {
  const channelsRef = useRef<Map<string, PusherChannel>>(new Map());
  const { bindChannelEvents } = usePusherChannelEvents({ pusherIntegrationConfig, publishPusherCallback });

  const subscribeToChannel = useCallback(
    (channel: string) => {
      const client = clientRef.current;
      if (!client || !channel) {
        console.error('Cannot subscribe: Pusher is not initialized or channel is missing');
        return;
      }

      const targetChannelName = `${PUSHER_CHANNEL_PREFIX}${channel}`;
      if (channelsRef.current.has(targetChannelName)) {
        // already subscribed to this channel.
        return;
      }

      const pusherChannel = client.subscribe(targetChannelName);
      bindChannelEvents(pusherChannel, channel);

      channelsRef.current.set(targetChannelName, pusherChannel);
    },
    [clientRef, bindChannelEvents],
  );

  const unsubscribeFromChannel = useCallback(
    (channel: string) => {
      const client = clientRef.current;
      if (!client || !channel) return;

      const targetChannelName = `${PUSHER_CHANNEL_PREFIX}${channel}`;
      const pusherChannel = channelsRef.current.get(targetChannelName);
      if (!pusherChannel) return;

      pusherChannel.unbind_all?.();
      client.unsubscribe?.(targetChannelName);
      channelsRef.current.delete(targetChannelName);
    },
    [clientRef],
  );

  const resetChannel = useCallback(() => {
    channelsRef.current.clear();
  }, []);

  return { channelsRef, subscribeToChannel, unsubscribeFromChannel, resetChannel } as const;
}
